import { ActionIcon, Badge, Button, Group, Popover, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconCloudCheck, IconCloudOff, IconCloudUp, IconRefresh } from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { useSyncState } from "../sync/actions";
import { syncNow } from "../sync/engine";
import { discardFailed, retryFailed } from "../sync/store";
import { describeOp } from "../sync/types";

dayjs.extend(relativeTime);

/** Header indicator: offline, changes waiting to sync, or changes the server rejected. */
export function SyncStatus() {
  const { pending, failed, status, persistent } = useSyncState();
  const offline = status.connection === "offline";

  let badge: React.ReactNode;
  if (failed.length > 0) {
    badge = (
      <Badge color="red" leftSection={<IconAlertTriangle size={12} />} data-testid="sync-status">
        {failed.length} not saved
      </Badge>
    );
  } else if (pending.length > 0) {
    badge = (
      <Badge color="yellow" leftSection={<IconCloudUp size={12} />} data-testid="sync-status">
        {pending.length} to sync
      </Badge>
    );
  } else if (offline) {
    badge = (
      <Badge color="gray" leftSection={<IconCloudOff size={12} />} data-testid="sync-status">
        Offline
      </Badge>
    );
  } else {
    badge = (
      <ActionIcon variant="subtle" color="gray" aria-label="All changes synced" data-testid="sync-status">
        <IconCloudCheck size={18} />
      </ActionIcon>
    );
  }

  return (
    <Popover position="bottom-end" width={320} withArrow shadow="md">
      <Popover.Target>
        <div style={{ cursor: "pointer" }}>{badge}</div>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            {pending.length === 0 && failed.length === 0
              ? "Everything is synced"
              : pending.length > 0
                ? `${pending.length} change${pending.length === 1 ? "" : "s"} waiting to sync`
                : "Some changes were not saved"}
          </Text>
          {!persistent && (
            <Text size="xs" c="red">
              This browser can't store changes offline; keep the app open until they sync.
            </Text>
          )}
          {status.needsSignIn && (
            <Text size="xs" c="red">
              Your session expired. Sign in again to send these changes.
            </Text>
          )}
          {offline && pending.length > 0 && (
            <Text size="xs" c="dimmed">
              They're saved on this device and will be sent when you're back online
              {status.nextRetryAt ? ` (next try ${dayjs(status.nextRetryAt).fromNow()})` : ""}.
            </Text>
          )}
          {pending.slice(0, 8).map((op) => (
            <Text key={op.seq} size="xs">
              • {describeOp(op)}
            </Text>
          ))}
          {pending.length > 8 && <Text size="xs">…and {pending.length - 8} more</Text>}
          {failed.map((op) => (
            <Stack key={op.seq} gap={2} p="xs" style={{ border: "1px solid var(--mantine-color-red-3)", borderRadius: 6 }}>
              <Text size="xs" fw={500}>
                {describeOp(op)}
              </Text>
              <Text size="xs" c="red">
                {op.lastError}
              </Text>
              <Group gap="xs">
                <Button size="compact-xs" variant="light" onClick={() => retryFailed(op).then(syncNow)}>
                  Try again
                </Button>
                <Button size="compact-xs" variant="subtle" color="red" onClick={() => discardFailed(op)}>
                  Discard
                </Button>
              </Group>
            </Stack>
          ))}
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              {status.lastSyncedAt ? `Last synced ${dayjs(status.lastSyncedAt).fromNow()}` : ""}
            </Text>
            {pending.length > 0 && (
              <Button size="compact-xs" variant="light" leftSection={<IconRefresh size={12} />} onClick={() => void syncNow()} loading={status.syncing}>
                Sync now
              </Button>
            )}
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
